import { useState, type FormEvent } from "react";
import { TENANT_ROLES } from "@gk/shared";
import { api } from "../../lib/api";
import { fmtDate, useAsync } from "../../lib/hooks";
import { useSession } from "../../lib/session";
import { ErrorNote, Modal } from "../../components/ui";
import { roleLabel } from "../../components/Shell";
import { AdminTabs } from "./common";

interface UserRow {
  id: string;
  email: string;
  name: string | null;
  platformAdmin: boolean;
  disabled: boolean;
  invited: boolean;
  lastLoginAt: string | null;
  lastLoginMfa: boolean | null;
  memberships: Array<{ tenantId: string; slug: string; name: string; role: string }>;
}

export function AdminUsersPage() {
  const { me } = useSession();
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const list = useAsync(() => api.get<UserRow[]>(`/api/admin/users${query ? `?q=${encodeURIComponent(query)}` : ""}`), [query]);
  const clients = useAsync(() => api.get<Array<{ id: string; name: string; status: string }>>("/api/admin/clients"), []);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await list.reload();
    } catch (err) {
      setError(err as Error);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Admin panel</h1>
          <p className="sub">Everyone with access to any client. Platform administrators are set in the deployment configuration, so a change there is reviewed like any other change.</p>
        </div>
      </div>
      <AdminTabs />
      <div className="stack" style={{ gap: 16 }}>
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            setQuery(q.trim());
          }}
        >
          <input className="input" style={{ maxWidth: 360 }} type="search" placeholder="Search by name or email" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search users" />
          <button className="btn">Search</button>
          <span className="spacer" />
          <button type="button" className="btn primary" onClick={() => setAdding(true)}>
            Add a person
          </button>
        </form>
        <ErrorNote error={error ?? list.error} />
        <section className="panel flush">
          <table className="table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Clients and roles</th>
                <th>Sign-in</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((u) => (
                <tr key={u.id}>
                  <td>
                    <b>{u.name ?? u.email}</b>
                    <div className="xs muted">{u.email}</div>
                    {u.platformAdmin && <span className="pill blue" style={{ marginTop: 4 }}>Platform admin</span>}
                  </td>
                  <td>
                    <div className="stack" style={{ gap: 6 }}>
                      {u.memberships.length === 0 && <span className="muted small">No client access</span>}
                      {u.memberships.map((m) => (
                        <div className="row" key={m.tenantId} style={{ gap: 8 }}>
                          <span className="small" style={{ minWidth: 140 }}>
                            {m.name}
                          </span>
                          <select
                            className="input compact"
                            aria-label={`Role in ${m.name}`}
                            value={m.role}
                            onChange={(e) => act(() => api.put(`/api/admin/users/${u.id}/memberships/${m.tenantId}`, { role: e.target.value }))}
                          >
                            {TENANT_ROLES.map((r) => (
                              <option key={r} value={r}>
                                {roleLabel(r)}
                              </option>
                            ))}
                          </select>
                          <button
                            className="btn small ghost"
                            onClick={() => window.confirm(`Remove ${u.email} from ${m.name}?`) && act(() => api.del(`/api/admin/users/${u.id}/memberships/${m.tenantId}`))}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="small">
                    {u.invited ? (
                      <span className="muted">Invited, not signed in yet</span>
                    ) : (
                      <>
                        {fmtDate(u.lastLoginAt)}
                        <div>{u.lastLoginMfa ? <span className="pill green">Two-step</span> : u.lastLoginMfa === false ? <span className="pill amber">No two-step</span> : null}</div>
                      </>
                    )}
                  </td>
                  <td>
                    {u.disabled ? <span className="pill red">Disabled</span> : <span className="pill green">Active</span>}
                    {u.id !== me?.user.id && (
                      <div style={{ marginTop: 6 }}>
                        <button
                          className={`btn small ${u.disabled ? "" : "danger"}`}
                          onClick={() =>
                            (u.disabled || window.confirm(`Disable ${u.email}? They are signed out everywhere and cannot sign in.`)) &&
                            act(() => api.patch(`/api/admin/users/${u.id}`, { disabled: !u.disabled }))
                          }
                        >
                          {u.disabled ? "Enable" : "Disable"}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
      {adding && (
        <AddPerson
          clients={(clients.data ?? []).filter((c) => c.status === "active")}
          onClose={() => setAdding(false)}
          onAdded={async () => {
            setAdding(false);
            await list.reload();
          }}
        />
      )}
    </>
  );
}

function AddPerson({ clients, onClose, onAdded }: { clients: Array<{ id: string; name: string }>; onClose: () => void; onAdded: () => void }) {
  const [f, setF] = useState({ email: "", name: "", tenantId: clients[0]?.id ?? "", role: "interviewer" });
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/admin/users", { ...f, name: f.name || undefined });
      onAdded();
    } catch (err) {
      setError(err as Error);
      setBusy(false);
    }
  };
  return (
    <Modal title="Add a person" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <label className="field">
          <span>Work email</span>
          <input className="input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
        </label>
        <label className="field">
          <span>Name (optional)</span>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={120} />
        </label>
        <div className="grid2">
          <label className="field">
            <span>Client</span>
            <select className="input" value={f.tenantId} onChange={(e) => setF({ ...f, tenantId: e.target.value })} required>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Role</span>
            <select className="input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
              {TENANT_ROLES.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="small muted">Their sign-in is created now. Tell them to open the login page, choose "First time, or forgot your password?", and follow the emailed link; they set up two-step verification at first sign-in.</p>
        <ErrorNote error={error} />
        <div className="row">
          <span className="spacer" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || !f.tenantId}>
            {busy ? "Adding…" : "Add person"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
